import React from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import type { TicketEmailTabProps } from './types';
import type { Email } from '../../types/api';

const TicketEmailTab: React.FC<TicketEmailTabProps> = ({
  email,
  loadingEmail,
  onUnlinkEmail,
  onOpenEmail,
  onReplyToEmail,
  onLinkEmail,
  allEmails,
  showAddEmail,
  onShowAddEmail,
  onHideAddEmail,
}) => {
  if (loadingEmail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (email) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Verknüpfte E-Mail</Typography>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            size="small"
            color="error"
            onClick={onUnlinkEmail}
          >
            Verknüpfung entfernen
          </Button>
        </Box>
        <List>
          <ListItem>
            <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <ListItemText
              primary={email.betreffText || 'Kein Betreff'}
              secondary={
                <>
                  <Typography component="span" variant="body2" color="text.primary">
                    Von: {email.absenderName || email.absenderEmail}
                  </Typography>
                  <br />
                  Empfangen: {new Date(email.empfangenAm).toLocaleString('de-DE')}
                </>
              }
            />
            <ListItemSecondaryAction>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={onReplyToEmail}
                  startIcon={<EmailIcon />}
                >
                  Antworten
                </Button>
                <IconButton onClick={onOpenEmail} title="E-Mail Details öffnen">
                  <OpenInNewIcon />
                </IconButton>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        </List>
        {email.textInhalt && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {email.textInhalt.substring(0, 500)}
              {email.textInhalt.length > 500 && '...'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Keine E-Mail mit diesem Ticket verknüpft.
      </Alert>
      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
        onClick={onShowAddEmail}
      >
        E-Mail hinzufügen
      </Button>

      {showAddEmail && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            E-Mail auswählen:
          </Typography>
          <Autocomplete
            options={allEmails}
            getOptionLabel={(option: Email) =>
              `${option.betreffText || 'Kein Betreff'} - ${option.absenderName || option.absenderEmail} (${new Date(option.empfangenAm).toLocaleDateString('de-DE')})`
            }
            renderInput={(params) => (
              <TextField {...params} label="E-Mail" placeholder="Suchen..." />
            )}
            onChange={(_, value) => {
              if (value) {
                onLinkEmail(value.emailID);
              }
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onHideAddEmail}>
              Abbrechen
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TicketEmailTab;
